import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { BASE_URL } from "../../constants/url";
import { setCredentials, logOut } from "../features/auth/authSlice";
import { persistor } from "../store";

const baseQuery = fetchBaseQuery({
  baseUrl: BASE_URL,
  credentials: "include",
  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth.token;
    if (token) {
      headers.set("authorization", `Bearer ${token}`);
    }
    return headers;
  },
});

const baseQueryWithReauth = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  // Handle 401 errors - session expired
  if (result?.error?.originalStatus === 401 || result?.error?.status === 401) {
    const errorData = result?.error?.data || {};
    const errorMessage = errorData?.message || errorData?.error || "";
    const messageString = typeof errorMessage === "string" ? errorMessage : "";
    
    if (
      messageString.includes("Authentication required") ||
      messageString.includes("session has been expired") ||
      messageString.includes("session expired")
    ) {
      // Session expired - logout and redirect to login
      // Note: RTK Query cache will be cleared on login page
      api.dispatch(logOut());
      
      // Clear all cache
      if (typeof window !== "undefined") {
        // Clear redux-persist storage
        persistor.purge();
        // Clear localStorage
        localStorage.clear();
        // Clear sessionStorage
        sessionStorage.clear();
        // Force redirect to login page
        window.location.href = "/login";
      }
      return result;
    }
  }

  if (result?.error?.originalStatus === 403) {
    // send refresh token to get new access token
    const refreshResult = await baseQuery(
      "/api/admin/auth/refresh-token",
      api,
      extraOptions
    );
    // console.log(refreshResult);
    if (refreshResult?.data) {
      const user = api.getState().auth.user;
      // store the new token
      api.dispatch(setCredentials({ ...refreshResult.data, user }));
      // retry the original query with new access token
      result = await baseQuery(args, api, extraOptions);
    } else {
      // Refresh token failed - logout
      // Note: RTK Query cache will be cleared on login page
      api.dispatch(logOut());
      
      // Clear all cache
      if (typeof window !== "undefined") {
        // Clear redux-persist storage
        persistor.purge();
        // Clear localStorage
        localStorage.clear();
        // Clear sessionStorage
        sessionStorage.clear();
      }
    }
  }

  return result;
};

export const apiSlice = createApi({
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Activity', 'Activities', 'ActivityStats', 'Document', 'Documents', 'DocumentStats', 'Company', 'Companies', 'CompanyStats', 'Form', 'Forms', 'FormStats', 'PrestartForm', 'PrestartForms'],
  endpoints: (builder) => ({}),
});
